// src/renderer/low-motion.ts
//
// Steam port T-6.7c follow-up — synchronous bridge between the persisted
// `settings.low_motion` value and the per-frame Pixi/HUD pulse renderers.
//
// Why not React context / Zustand? The renderers in `src/renderer/*` run
// inside the Pixi ticker (called by `requestAnimationFrame`), not inside
// React's render tree. They need a fast, allocation-free, synchronous
// read every frame. The Settings UI already mirrors `low_motion` to
// localStorage at key `rogues-climb-accessibility-settings` (see
// `accessibility-persistence.ts`); this module reads that mirror at boot
// and listens for a custom `'rc-low-motion-change'` DOM event so updates
// propagate without a polling loop.
//
// The actual luminance clamp is delegated to
// `clampLuminanceDelta` from `accessibility-options.ts` — DO NOT
// reimplement; the helper is locked by `tests/unit/frenzy-luminance.test.ts`.

import {
  clampLuminanceDelta,
  MAX_LUMINANCE_DELTA,
} from '../ui/settings/accessibility-options.js';

/** localStorage key the Settings UI mirrors `low_motion` into. Must match
 *  `LOCAL_KEY` in `accessibility-persistence.ts`. */
const LOCAL_KEY = 'rogues-climb-accessibility-settings';

/** DOM event the persistence layer emits when `low_motion` changes. The
 *  Settings UI dispatches it after every `persistAccessibilitySettings`
 *  call so the renderer + HUD subscribers update inside the same frame. */
export const LOW_MOTION_CHANGE_EVENT = 'rc-low-motion-change';

/** In-process cache so the per-frame getter is allocation-free and
 *  doesn't hit `JSON.parse` 60+ times a second. */
let cached: boolean | null = null;
let initialized = false;
const subscribers = new Set<(value: boolean) => void>();

function readFromStorage(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { low_motion?: unknown };
    return parsed && typeof parsed === 'object' && parsed.low_motion === true;
  } catch {
    return false;
  }
}

function ensureInitialized(): void {
  if (initialized) return;
  initialized = true;
  cached = readFromStorage();
  if (typeof window !== 'undefined') {
    window.addEventListener(LOW_MOTION_CHANGE_EVENT, () => {
      const prev = cached;
      cached = readFromStorage();
      if (prev !== cached) {
        for (const cb of subscribers) cb(cached);
      }
    });
  }
}

/** Synchronous read of the current `settings.low_motion` value. Cheap
 *  enough to call from inside the Pixi ticker every frame. */
export function getLowMotion(): boolean {
  ensureInitialized();
  return cached === true;
}

/** Subscribe to low-motion changes. The callback fires only on
 *  transitions (true→false / false→true), never on no-op writes. Returns
 *  an unsubscribe function. Used by the HUD React components to swap
 *  their CSS keyframes inline when the user toggles the setting. */
export function subscribeLowMotion(cb: (value: boolean) => void): () => void {
  ensureInitialized();
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/**
 * Per-frame alpha pulse clamp. When `low_motion === false` (the default),
 * returns `target` unchanged — visual output is identical to pre-T-6.7c
 * behavior so existing goldens still pass. When `low_motion === true`,
 * delegates to `clampLuminanceDelta` so the frame-to-frame alpha delta
 * stays under W3C 2.3.3's 10% photosensitive-epilepsy threshold.
 *
 * Alpha modulates luminance proportionally for a fixed underlying tint,
 * so clamping alpha-delta is the right knob for the existing
 * `0.3 * pulse` style call sites in `board-renderer.ts`,
 * `game-renderer.ts`, and `cannon-renderer.ts` — all of which oscillate
 * a single tint's alpha via `Math.sin`.
 *
 * O(1) per frame, no allocations.
 */
export function applyLowMotionPulse(
  previousAlpha: number,
  targetAlpha: number,
): number {
  if (!getLowMotion()) return targetAlpha;
  return clampLuminanceDelta(previousAlpha, targetAlpha, MAX_LUMINANCE_DELTA);
}

// ---------------------------------------------------------------------------
// Test-only resets — keep unit tests deterministic across runs.
// ---------------------------------------------------------------------------

/** **Test-only.** Force the cached value to a specific boolean and notify
 *  subscribers. Bypasses localStorage so tests don't have to stub the
 *  full `'settings'` slot shape. */
export function _setLowMotionForTest(value: boolean): void {
  initialized = true;
  const prev = cached;
  cached = value;
  if (prev !== cached) {
    for (const cb of subscribers) cb(cached);
  }
}

/** **Test-only.** Reset module state — clears cache, drops subscribers,
 *  and forces the next `getLowMotion()` call to re-read storage. */
export function _resetLowMotionForTest(): void {
  cached = null;
  initialized = false;
  subscribers.clear();
}
